import '../../../styles/hide-scrollbar.css';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddProjectModal from '../../../components/modals/AddProjectModal.tsx';
import { Plus, FolderOpen, Calendar, Target, BarChart3, ChevronDown, ChevronUp, Info, Edit, Trash2, MoreVertical, CheckCircle, Circle, Play, Clock, AlertTriangle, Bot } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import { projectTaskService, ProjectWithTasks } from '../../../services/projectTaskService.ts';
import { personalProjectsApi } from '../../../services/personalProjectsApi.ts';

const API_BASE_URL = () => {
  // Check if we're in production
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }
  
  // Development environment
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const PersonalProjects: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [projects, setProjects] = useState<Array<any>>([]);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showProgressInfoDialog, setShowProgressInfoDialog] = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotionItem, setPromotionItem] = useState<any>(null);
  const [initialProjectData, setInitialProjectData] = useState<any>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [newTaskTitle, setNewTaskTitle] = useState<{ [projectId: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState<{ [id: string]: boolean }>({});

  // Mock funnel stats
  const funnelStats = {
    ideas: 12, // This would come from ideas API
    projects: projects.length,
    tasks: projects.reduce((total, project) => total + (project.tasks?.length || 0), 0),
    totalCompleted: projects.filter(p => p.status === 'Completed').length + 
                   projects.reduce((total, project) => total + (project.tasks?.filter((t: any) => t.completed).length || 0), 0)
  };

  useEffect(() => {
    fetchProjects();
    
    // Check if we came from ideas page with data
    if (location.state?.openModal && location.state?.ideaData) {
      setInitialProjectData(location.state.ideaData);
      setShowAddProjectModal(true);
      
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
    
    // Check if we need to highlight a specific project
    if (location.state?.highlightProject) {
      const projectId = location.state.highlightProject;
      // Scroll to the project after a short delay to ensure it's rendered
      setTimeout(() => {
        const projectElement = document.getElementById(`project-${projectId}`);
        if (projectElement) {
          projectElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Add a temporary highlight effect
          projectElement.classList.add('ring-4', 'ring-[color:var(--lyra-color-primary)]', 'ring-opacity-50');
          setTimeout(() => {
            projectElement.classList.remove('ring-4', 'ring-[color:var(--lyra-color-primary)]', 'ring-opacity-50');
          }, 3000);
        }
      }, 100);
      
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Separate useEffect for handling openProjectModal after projects are loaded
  useEffect(() => {
    if (location.state?.openProjectModal && location.state?.projectId && projects.length > 0) {
      const projectId = location.state.projectId;
      const project = projects.find(p => p._id === projectId);
      if (project) {
        // Convert project to the format expected by AddProjectModal
        const projectData = {
          id: project._id,
          title: project.title || project.name,
          description: project.description,
          category: project.category,
          tags: project.tags || [],
          priority: project.priority || 'Medium',
          status: project.status,
          dueDate: project.dueDate,
          tasks: project.tasks || []
        };
        setInitialProjectData(projectData);
        setShowAddProjectModal(true);
        
        // Clear the state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [projects, location.state]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && !(event.target as Element).closest('.relative')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsWithTasks = await projectTaskService.getProjectsWithTasks();
      setProjects(projectsWithTasks);
      console.log('Fetched projects with tasks:', projectsWithTasks);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async (newProject: any) => {
    try {
      console.log('DEBUG: handleAddProject called with:', newProject);
      const projectData = await personalProjectsApi.createProject(newProject);
      console.log('DEBUG: Project created successfully:', projectData);
      // Use tasks from response if present
      const projectWithTasks: ProjectWithTasks = {
        ...(projectData as any),
        tasks: (projectData as any).tasks || newProject.tasks || []
      };
      setProjects(prev => [projectWithTasks, ...prev]);
      setShowAddProjectModal(false);
      setInitialProjectData(null);
    } catch (error) {
      console.error('Error adding project:', error);
      alert('Failed to add project');
    }
  };

  const handleUpdateProject = async (projectId: string, updates: any) => {
    try {
      console.log('DEBUG: handleUpdateProject called with:', projectId, updates);
      const updatedProjectData = await personalProjectsApi.updateProject(projectId, updates);
      // Use tasks from response if present
      const projectWithTasks: ProjectWithTasks = {
        ...(updatedProjectData as any),
        tasks: (updatedProjectData as any).tasks || updates.tasks || []
      };
      setProjects(prev => prev.map(p => p._id === projectId ? projectWithTasks : p));
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    console.log('Delete project clicked:', projectId);
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await personalProjectsApi.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p._id !== projectId));
      console.log('Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const handleEditProject = (project: any) => {
    console.log('Edit project clicked:', project);
    setEditingProject(project);
    setInitialProjectData({
      id: project._id,
      title: project.title,
      description: project.description,
      category: project.category,
      status: project.status,
      dueDate: project.dueDate,
      tasks: project.tasks
    });
    setShowAddProjectModal(true);
    console.log('Modal should open now');
  };

  // Calculate progress based on completed tasks and their weights
  const calculateProgress = (project: any): number => {
    const tasks = Array.isArray(project.tasks) ? project.tasks : [];
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(task => task.status === 'Done').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // Toggle task completion
  const toggleTask = (projectId: string, taskId: number) => {
    const project = projects.find(p => p._id === projectId);
    if (!project) return;
    
    const updatedTasks = project.tasks.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    
    handleUpdateProject(projectId, { tasks: updatedTasks });
  };

  // Toggle expanded tasks view for a project
  const toggleExpandedTasks = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // Handle task status change
  const handleTaskStatusChange = async (taskId: string, currentStatus: string) => {
    try {
      const statusFlow = {
        'Todo': 'In Progress',
        'In Progress': 'Completed',
        'Review': 'Completed',
        'Completed': 'Todo',
        'Blocked': 'Todo'
      };
      
      const newStatus = statusFlow[currentStatus as keyof typeof statusFlow] || 'Todo';
      
      await projectTaskService.updateTaskStatus(taskId, newStatus as any);
      
      // Refresh projects to show updated task status and progress
      await fetchProjects();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  // Add new task to project
  const handleAddTask = async (projectId: string, taskTitle: string) => {
    if (!taskTitle.trim()) return;
    
    try {
      const project = projects.find(p => p._id === projectId);
      await projectTaskService.createProjectTask(projectId, {
        title: taskTitle.trim(),
        description: '',
        priority: 'Medium',
        category: project?.category || 'Work',
        estimatedTime: '1h',
        sourceIdeaId: project?.sourceIdeaId,
        dueDate: project?.dueDate || ''
      }, project?.dueDate || '');
      
      // Clear the input and refresh projects
      setNewTaskTitle(prev => ({ ...prev, [projectId]: '' }));
      await fetchProjects();
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    
    try {
      await projectTaskService.deleteTask(taskId);
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Handle promoting project to tasks
  const handlePromoteProject = (projectId: string, fromStage: string, toStage: string) => {
    const project = projects.find(p => p._id === projectId);
    if (project) {
      setPromotionItem({
        id: projectId,
        title: project.title,
        description: project.description,
        category: project.category,
        tags: project.tags || [],
        priority: project.priority || 'Medium'
      });
      setShowPromotionModal(true);
    }
  };

  const handleConfirmPromotion = async (promotionData: any) => {
    try {
      console.log('Breaking down project into tasks:', promotionData);
      
      // Here you would call the task creation API
      // For now, just close the modal
      setShowPromotionModal(false);
      setPromotionItem(null);
      
      // Optionally update the project status
      if (promotionData.fromStage === 'project') {
        // Mark project as "Broken Down" or similar status
      }
    } catch (error) {
      console.error('Error breaking down project:', error);
    }
  };

  // Handle edit tasks from a project
  const handleEditTasks = (project: any) => {
    navigate('/tools/project-management/personal-kanban', {
      state: {
        openModal: true,
        projectData: {
          id: project._id,
          name: project.title || project.name,
          description: project.description,
          tasks: project.tasks || [],
          sourceIdeaId: project.sourceIdeaId || undefined // Pass through the original idea ID
        }
      }
    });
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'On Hold': return 'bg-[color:var(--lyra-color-surface-2)] text-gray-800';
      default: return 'bg-[color:var(--lyra-color-surface-2)] text-gray-800';
    }
  };

  // Get progress color
  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Todo': return '📋';
      case 'InProgress': return '⏳';
      case 'Done': return '✅';
      default: return '📋';
    }
  };

    // ── Design tokens (matches Workflows.jsx exactly) ────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif", padding: "28px 32px 60px" }}>
      <PageTracker pageTitle="My Projects" category="project-management" />
      <div className="mb-8">
        <div className='flex flex-row justify-between items-center mb-4'>
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>My Projects</div>
            <p style={{ color: T.ink4 }} className='text-sm'>
              Manage and track your personal projects, side hustles, and passion initiatives.
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/tools/project-management/project-agent')}
              className="bg-[color:var(--lyra-color-primary)] text-[color:var(--lyra-color-surface)] px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[color:var(--app-primary-deep)] text-sm font-semibold transition-colors"
            >
              <Bot size={18} />
              <span style={{ color: "var(--app-on-accent)" }}>Ask Agent</span>
            </button>

            <button className="bg-[color:var(--lyra-color-primary)] text-[color:var(--lyra-color-surface)] px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[color:var(--app-primary-deep)] text-sm font-semibold transition-colors"
              onClick={() => setShowAddProjectModal(true)}
              style={{ background: T.accent }}
              >
              <Plus size={20} />
              <span style={{ color: "var(--app-on-accent)" }}>New Project</span>
            </button>
          </div>
        </div>

        {/* Compact stats row — was a full-width colored banner, trimmed down
            since the calculation detail is one click away, not something
            that needs permanent real estate. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-[color:var(--lyra-color-primary)]">{projects.length}</span>
            <span className="text-[color:var(--lyra-color-text-muted)]">Projects</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-green-600">
              {projects.length > 0 ? Math.round(projects.reduce((sum, p) => sum + calculateProgress(p.tasks), 0) / projects.length) : 0}%
            </span>
            <span className="text-[color:var(--lyra-color-text-muted)]">avg progress</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-yellow-600">
              {projects.reduce((sum, p) => sum + p.tasks.filter(t => t.completed).length, 0)}
            </span>
            <span className="text-[color:var(--lyra-color-text-muted)]">tasks done</span>
          </div>
          <button
            onClick={() => setShowProgressInfoDialog(true)}
            className="flex items-center gap-1 text-[color:var(--lyra-color-text-muted)] hover:text-[color:var(--lyra-color-primary)] transition-colors"
            title="Learn more about progress calculation"
          >
            <Info size={13} />
            <span>How progress is calculated</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--lyra-color-primary)]"></div>
            <span className="ml-2 text-[color:var(--lyra-color-text-muted)]">Loading projects...</span>
          </div>
        ) : (
          projects.map((project) => {
            const progress = calculateProgress(project);
            const completedTasks = project.tasks.filter(task => task.status === 'Completed').length;
            const totalTasks = project.tasks.length;
            const isExpanded = expandedProjects.has(project._id);
            const visibleTasks = isExpanded ? project.tasks : project.tasks.slice(0, 3);
            
            console.log('Rendering project:', project._id, 'activeDropdown:', activeDropdown);
            
            return (
              <div 
                key={project._id} 
                id={`project-${project._id}`}
                className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <FolderOpen className="text-[color:var(--lyra-color-primary)]" size={20} />
                    <span className="text-sm font-medium text-[color:var(--lyra-color-primary)] bg-[color:var(--app-primary-soft)] px-2 py-1 rounded">
                      {project.category}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                    
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Dropdown button clicked for project:', project._id);
                          setActiveDropdown(activeDropdown === project._id ? null : project._id);
                        }}
                        className="p-1.5 rounded-lg text-[color:var(--app-text-3)] hover:text-[color:var(--lyra-color-text-muted)] hover:bg-[color:var(--lyra-color-surface)] transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {activeDropdown === project._id && (
                        <div 
                          className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border border-[color:var(--lyra-color-border)] py-1 w-32"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProject(project);
                              setActiveDropdown(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-[color:var(--app-text-2)] hover:bg-[color:var(--lyra-color-surface)] flex items-center gap-2"
                          >
                            <Edit className="w-3 h-3" />
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project._id);
                              setActiveDropdown(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-2">{project.title}</h3>
                
                {/* Project Description with line clamp and show more */}
                <div>
                  <p
                    className={`text-[color:var(--lyra-color-text-muted)] text-sm mb-4 ${showFullDescription[project._id] ? '' : 'line-clamp-4'}`}
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {project.description}
                  </p>
                  {project.description && project.description.length > 0 && (
                    <button
                      className="text-[color:var(--lyra-color-primary)] text-xs underline mb-2 focus:outline-none"
                      onClick={() => setShowFullDescription(prev => ({ ...prev, [project._id]: !prev[project._id] }))}
                    >
                      {showFullDescription[project._id] ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-[color:var(--lyra-color-text-muted)] mb-1">
                    <span>Progress</span>
                    <span>{progress}% ({completedTasks}/{totalTasks} tasks)</span>
                  </div>
                  <div className="w-full bg-[color:var(--app-surface-3)] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progress)}`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Task List */}
                <div className="mb-4">
                  <div className="max-h-40 overflow-y-auto border border-[color:var(--lyra-color-border)] rounded-lg divide-y divide-[color:var(--lyra-color-border)]">
                    {Array.isArray(project.tasks) && project.tasks.length > 0 ? (
                      project.tasks.map(task => (
                        <div key={task._id || task.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-[color:var(--lyra-color-surface)] cursor-pointer">
                          <span className="text-[color:var(--app-text-2)] truncate">{task.title || task.name}</span>
                          <span className="flex-shrink-0">{getStatusIcon(task.status)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-[color:var(--app-text-3)] text-xs px-3 py-3 text-center">No tasks yet.</div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-[color:var(--app-text-3)]">
                    <Calendar size={16} className="mr-1" />
                    <span>Due: {new Date(project.dueDate).toLocaleDateString()}</span>
                  </div>
                  <h4 className="text-sm font-normal text-[color:var(--app-text-3)] mb-2 flex items-center">
                    <BarChart3 className="w-4 h-4 mr-1" />
                    Tasks ({completedTasks}/{totalTasks})
                  </h4>
                </div>
                
                {/* Edit / Create Tasks Button */}
                <div className="mt-4 pt-4 border-t border-[color:var(--lyra-color-border)]">
                  <button
                    onClick={() => handleEditTasks(project)}
                    className="w-full bg-[color:var(--lyra-color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[color:var(--app-primary-deep)] transition-colors flex items-center justify-center space-x-2 text-sm font-semibold"
                  >
                    <Plus size={16} />
                    <span style={{ color: "var(--app-on-accent)" }}>{totalTasks === 0 ? 'Create Tasks' : 'Edit Tasks'}</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
        
        <div className="bg-[color:var(--lyra-color-surface)] rounded-lg border-2 border-dashed border-[color:var(--app-border-strong)] p-6 flex flex-col items-center justify-center text-[color:var(--app-text-3)] hover:border-gray-400 cursor-pointer transition-colors"
          onClick={() => setShowAddProjectModal(true)}>
          <Plus size={32} className="mb-2" />
          <span className="text-lg font-medium">Add New Project</span>
          <span className="text-sm">Start tracking a new personal project</span>
        </div>
      </div>
      
      {/* Add Project Modal */}
      <AddProjectModal
        isOpen={showAddProjectModal}
        onClose={() => {
          setShowAddProjectModal(false);
          setInitialProjectData(null); // Clear initial data when closing
        }}
        onAdd={initialProjectData?.status ? 
          (updatedProject: any) => handleUpdateProject(initialProjectData.id, updatedProject) : 
          handleAddProject
        }
        initialData={initialProjectData}
      />

      {/* Progress Info Dialog */}
      {showProgressInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowProgressInfoDialog(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] flex items-center space-x-2">
                <span>📊</span>
                <span>How Progress is Calculated</span>
              </h3>
              <button
                onClick={() => setShowProgressInfoDialog(false)}
                className="text-[color:var(--app-text-3)] hover:text-[color:var(--lyra-color-text-muted)] transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 text-sm text-[color:var(--app-text-2)]">
              <div>
                <h4 className="font-semibold text-[color:var(--lyra-color-text)] mb-1">Weighted Task System</h4>
                <p>Each task has a weight (percentage contribution to project)</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-[color:var(--lyra-color-text)] mb-1">Formula</h4>
                <p className="font-mono bg-[color:var(--lyra-color-surface-2)] p-2 rounded">
                  Progress = (Sum of completed task weights / Total task weights) × 100
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-[color:var(--lyra-color-text)] mb-1">Example</h4>
                <p>If you complete tasks worth 65% weight out of 100% total → 65% progress</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-[color:var(--lyra-color-text)] mb-1">Interactive</h4>
                <p>Click on any task to toggle its completion status</p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowProgressInfoDialog(false)}
                className="bg-[color:var(--lyra-color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[color:var(--app-primary-deep)] transition-colors text-sm font-semibold"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalProjects;
